import React, { useState } from 'react';
import { Delivery } from '../types';

interface DeliveryCardProps {
  delivery: Delivery;
  onWeightUpdate?: (id: number, weight: number) => void;
  onSendSMS?: (id: number, phone: string) => void;
}

const DeliveryCard: React.FC<DeliveryCardProps> = ({ 
  delivery, 
  onWeightUpdate = () => {}, 
  onSendSMS = () => {} 
}) => {
  const [weight, setWeight] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  
  const handleWeightSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const weightValue = parseFloat(weight);
    if (!isNaN(weightValue) && weightValue > 0) {
      setIsUpdating(true);
      
      // Simuler une mise à jour avec un délai
      setTimeout(() => {
        onWeightUpdate(delivery.id, weightValue);
        setIsUpdating(false);
        setWeight('');
        
        // Afficher notification de succès
        setShowNotification(true);
        setTimeout(() => setShowNotification(false), 3000);
      }, 800);
    }
  };
  
  const handleSendSMS = () => {
    onSendSMS(delivery.id, delivery.contactPhone);
  };
  
  // Formater la date pour l'affichage
  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };
  
  // Déterminer la classe de couleur en fonction du statut
  const getStatusClass = () => {
    switch(delivery.status) {
      case 'en_cours': return 'bg-green-100 text-green-800 border-green-200';
      case 'en_attente': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'terminée': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'planifiée': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  
  return (
    <div className={`border ${getStatusClass()} rounded-lg shadow-sm p-4 transition-all hover:shadow-md`}>
      <div className="flex justify-between items-start">
        <h3 className="font-bold text-lg">{delivery.clientName}</h3>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          delivery.status === 'en_cours' ? 'bg-green-200 text-green-800' : 
          delivery.status === 'en_attente' ? 'bg-yellow-200 text-yellow-800' : 
          delivery.status === 'terminée' ? 'bg-gray-200 text-gray-800' : 
          'bg-blue-200 text-blue-800'
        }`}>
          {delivery.status.replace('_', ' ')}
        </span>
      </div>
      
      <div className="mt-3 space-y-2">
        <div className="flex items-start">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="text-sm text-gray-700">{delivery.destination.address}</p>
        </div>
        
        <div className="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm">
            <span className="text-gray-700">Prévu: </span>
            <span className="font-medium">{formatTime(delivery.scheduledTime)}</span>
            <span className="mx-2 text-gray-500">|</span>
            <span className="text-gray-700">Estimé: </span>
            <span className="font-medium">{formatTime(delivery.estimatedArrival)}</span>
          </div>
        </div>
        
        <div className="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <span className="text-sm text-gray-700">{delivery.cargo}</span>
        </div>
      </div>
      
      {/* Formulaire de mise à jour du poids */}
      <div className="mt-4 border-t border-gray-200 pt-4">
        <form onSubmit={handleWeightSubmit} className="flex items-center">
          <div className="flex-grow">
            <label htmlFor={`weight-${delivery.id}`} className="block text-sm font-medium text-gray-700 mb-1">
              Poids de la marchandise (kg)
            </label>
            <input
              type="number"
              id={`weight-${delivery.id}`}
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder="Saisir le poids"
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              min="0"
              step="0.1"
              disabled={isUpdating}
            />
          </div>
          <button
            type="submit"
            className={`ml-3 mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
              isUpdating ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
            disabled={isUpdating}
          >
            {isUpdating ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Mise à jour...
              </>
            ) : 'Mettre à jour'}
          </button>
        </form>
        
        {/* Notification de succès */}
        {showNotification && (
          <div className="mt-3 bg-green-50 border border-green-200 rounded-md p-3 animate-pulse">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  Poids mis à jour avec succès et synchronisé avec LORIE
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Bouton d'envoi de SMS */}
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={handleSendSMS}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
          Envoyer SMS au client
        </button>
      </div>
    </div>
  );
};

export default DeliveryCard;
